import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CopyPlus, Download, ShieldCheck, ShieldOff } from 'lucide-react';
import { useId, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { useAuth } from '../auth/AuthProvider';
import { atWallClock, clock } from '../playback/time';
import { formatBytes } from '../system/media';
import {
  copiesKey,
  createCopy,
  createExport,
  createHold,
  exportsKey,
  HOLD_MAX_MS,
  holdValidityDays,
  holdsKey,
  releaseHold,
  type ExportFormat,
  type Hold,
} from './api';

const MIN_REASON = 3;
const MAX_REASON = 500;

function reasonError(reason: string): string | undefined {
  const n = reason.trim().length;
  if (n === 0) return 'Informe o motivo.';
  if (n < MIN_REASON) return `Use pelo menos ${String(MIN_REASON)} caracteres.`;
  if (n > MAX_REASON) return `Use no máximo ${String(MAX_REASON)} caracteres.`;
  return undefined;
}

function problemOf(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'Você não tem permissão para esta ação.';
    if (err.status === 404) return 'O registro não existe mais.';
    if (err.code === 'hold_released') return 'Esta evidência já foi liberada.';
    if (err.code === 'copy_in_progress')
      return 'Já há uma cópia deste equipamento em andamento. Aguarde ela terminar.';
    if (err.code === 'recording_unavailable')
      return 'Esta instalação não tem armazenamento de gravação.';
    if (err.code === 'validation_failed') return 'Revise os campos destacados.';
  }
  return 'Não foi possível concluir. Tente novamente.';
}

function Modal({
  title,
  icon,
  children,
  onClose,
  onSubmit,
  submitLabel,
  busy,
  danger = false,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  busy: boolean;
  danger?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, onClose);
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    onSubmit();
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <form onSubmit={submit} noValidate>
          <div className="flex items-center gap-3">
            <div
              className={`grid size-8 shrink-0 place-items-center rounded-full ${danger ? 'bg-crit-soft text-crit' : 'bg-warn-soft text-warn'}`}
            >
              {icon}
            </div>
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              {title}
            </h2>
          </div>
          <div className="mt-4 space-y-4">{children}</div>
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              className={danger ? 'bg-crit! hover:bg-crit/90!' : ''}
            >
              {busy ? 'Aguarde…' : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReasonField({
  label,
  hint,
  value,
  error,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  error: string | undefined;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          data-autofocus
          rows={3}
          maxLength={MAX_REASON}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className={`${inputClass} h-auto py-2`}
        />
      )}
    </Field>
  );
}

interface RangeProps {
  deviceId: string;
  deviceName: string;
  day: string;
  dayRange: [number, number];
  initial: [number, number];
  timeZone: string | undefined;
  onClose: () => void;
}

/**
 * A range of one day's recordings (wall-clock times of `day` in the tenant
 * zone) plus a reason, submitted by `send`.
 */
function RangeDialog<T>({
  day,
  dayRange,
  initial,
  timeZone,
  onClose,
  title,
  icon,
  intro,
  submitLabel,
  maxLabel,
  maxMs,
  options,
  send,
  onDone,
}: Omit<RangeProps, 'deviceId' | 'deviceName'> & {
  title: string;
  icon: ReactNode;
  intro: ReactNode;
  /** Extra choices shown between the range and the reason. */
  options?: ReactNode;
  submitLabel: string;
  maxLabel: string;
  /** Longest range accepted, checked before sending. */
  maxMs?: number;
  send: (from: number, to: number, reason: string) => Promise<T>;
  onDone: (result: T) => void;
}) {
  const [fromText, setFromText] = useState(clock(initial[0], timeZone));
  const [toText, setToText] = useState(clock(initial[1], timeZone));
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);

  const from = atWallClock(day, fromText, timeZone);
  // "Até 00:00" means the end of the day.
  const to =
    toText === '00:00:00' || toText === '00:00' ? dayRange[1] : atWallClock(day, toText, timeZone);
  const rangeError =
    from === null || to === null
      ? 'Use o formato HH:MM:SS.'
      : to <= from
        ? 'O fim deve ser depois do início.'
        : maxMs !== undefined && to - from > maxMs
          ? `Intervalo longo demais (${maxLabel}).`
          : undefined;

  const save = useMutation({
    mutationFn: () => send(from ?? 0, to ?? 0, reason.trim()),
    onSuccess: (r) => {
      onDone(r);
      onClose();
    },
  });

  const fieldErr = save.error instanceof ApiError ? save.error.fields : {};
  const submit = () => {
    setTried(true);
    if (rangeError || reasonError(reason)) return;
    save.mutate();
  };

  return (
    <Modal
      title={title}
      icon={icon}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={submitLabel}
      busy={save.isPending}
    >
      <p className="text-[13px] leading-relaxed text-fg-muted">{intro}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Início" error={tried ? rangeError : undefined}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="time"
              step={1}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={fromText}
              onChange={(e) => {
                setFromText(e.target.value);
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Fim" error={fieldErr.to ? `Intervalo inválido (${maxLabel}).` : undefined}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="time"
              step={1}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={toText}
              onChange={(e) => {
                setToText(e.target.value);
              }}
              className={inputClass}
            />
          )}
        </Field>
      </div>
      {options}
      <ReasonField
        label="Motivo"
        hint="Ex.: ocorrência, número do boletim, pedido do cliente. Fica registrado na auditoria."
        value={reason}
        error={tried || fieldErr.reason ? reasonError(reason) : undefined}
        onChange={setReason}
      />
      <FormProblem message={save.isError ? problemOf(save.error) : null} />
    </Modal>
  );
}

/** Protects part of a day's recordings from retention. */
export function ProtectDialog({ deviceId, deviceName, initial, ...rest }: RangeProps) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [days, setDays] = useState(90);
  // Evidence is at most 30 minutes: a longer selection starts trimmed.
  const start: [number, number] = [initial[0], Math.min(initial[1], initial[0] + HOLD_MAX_MS)];
  return (
    <RangeDialog
      {...rest}
      initial={start}
      title="Proteger como evidência"
      icon={<ShieldCheck aria-hidden="true" className="size-4" strokeWidth={2} />}
      intro={
        <>
          As gravações de <strong className="text-fg">{deviceName || 'esta câmera'}</strong> neste
          intervalo (até 30 minutos) não serão apagadas pela retenção enquanto a evidência valer.
          Depois disso, voltam a seguir os dias de gravação da câmera.
        </>
      }
      options={<ValidityField days={days} onChange={setDays} />}
      submitLabel="Proteger"
      maxLabel="máximo 30 minutos"
      maxMs={HOLD_MAX_MS}
      send={(from, to, reason) => createHold(request, deviceId, from, to, reason, days)}
      onDone={(h) => {
        void qc.invalidateQueries({ queryKey: holdsKey });
        notify(
          h.segments === 0
            ? 'Evidência registrada. Ainda não há gravações no intervalo.'
            : `Evidência protegida: ${String(h.segments)} ${h.segments === 1 ? 'trecho' : 'trechos'} (${formatBytes(h.bytes)}).`,
        );
      }}
    />
  );
}

function ValidityField({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <Field label="Validade">
      {({ id, describedBy }) => (
        <select
          id={id}
          aria-describedby={describedBy}
          value={days}
          onChange={(e) => {
            onChange(Number(e.target.value));
          }}
          className={inputClass}
        >
          {holdValidityDays.map((d) => (
            <option key={d} value={d}>
              {d === 365 ? '1 ano' : `${String(d)} dias`}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/**
 * Copies part of the device's own recording (DVR/NVR) into the platform as
 * evidence (ADR-042). The device plays it at normal speed, so it takes as long
 * as the range.
 */
export function CopyDialog({ deviceId, deviceName, initial, ...rest }: RangeProps) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [days, setDays] = useState(90);
  const start: [number, number] = [initial[0], Math.min(initial[1], initial[0] + HOLD_MAX_MS)];
  return (
    <RangeDialog
      {...rest}
      initial={start}
      title="Copiar para o servidor"
      icon={<CopyPlus aria-hidden="true" className="size-4" strokeWidth={2} />}
      intro={
        <>
          O trecho gravado em{' '}
          <strong className="text-fg">{deviceName || 'este equipamento'}</strong> (até 30 minutos) é
          copiado para o Pro Guard e fica protegido como evidência. A cópia leva o mesmo tempo do
          trecho, porque o equipamento entrega o vídeo em velocidade normal.
        </>
      }
      options={<ValidityField days={days} onChange={setDays} />}
      submitLabel="Copiar"
      maxLabel="máximo 30 minutos"
      maxMs={HOLD_MAX_MS}
      send={(from, to, reason) => createCopy(request, deviceId, from, to, reason, days)}
      onDone={() => {
        void qc.invalidateQueries({ queryKey: copiesKey });
        notify('Cópia iniciada. Acompanhe em Evidências → Cópias do equipamento.');
      }}
    />
  );
}

const formatOptions: { value: ExportFormat; title: string; detail: string }[] = [
  {
    value: 'zip',
    title: 'ZIP com originais',
    detail:
      'Vídeos gravados sem alteração e manifesto com o SHA-256 de cada um. Para cadeia de custódia.',
  },
  {
    value: 'mp4',
    title: 'MP4 único',
    detail:
      'Um só vídeo recortado no intervalo, para compartilhar. Lacunas de gravação ficam emendadas.',
  },
];

function FormatChoice({
  value,
  onChange,
}: {
  value: ExportFormat;
  onChange: (f: ExportFormat) => void;
}) {
  const name = useId();
  return (
    <div role="radiogroup" aria-label="Formato" className="grid gap-2 sm:grid-cols-2">
      {formatOptions.map((o) => (
        <label
          key={o.value}
          className={`flex cursor-pointer flex-col gap-0.5 rounded-md border px-3 py-2 transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
            value === o.value
              ? 'border-accent bg-accent-soft'
              : 'border-line-strong bg-canvas hover:border-fg-subtle'
          }`}
        >
          <span className="flex items-center gap-2 text-[13px] font-medium text-fg">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={() => {
                onChange(o.value);
              }}
              className="size-3.5 accent-[var(--color-accent-strong)]"
            />
            {o.title}
          </span>
          <span className="text-xs text-fg-subtle">{o.detail}</span>
        </label>
      ))}
    </div>
  );
}

/** Queues an export of part of a day's recordings (built in the background). */
export function ExportDialog({ deviceId, deviceName, ...rest }: RangeProps) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [format, setFormat] = useState<ExportFormat>('zip');
  return (
    <RangeDialog
      {...rest}
      title="Exportar gravação"
      icon={<Download aria-hidden="true" className="size-4" strokeWidth={2} />}
      intro={
        <>
          Gera um arquivo com as gravações de{' '}
          <strong className="text-fg">{deviceName || 'esta câmera'}</strong> no intervalo. Ele fica
          disponível em Evidências → Exportações.
        </>
      }
      options={<FormatChoice value={format} onChange={setFormat} />}
      submitLabel="Exportar"
      maxLabel="máximo 24 horas"
      send={(from, to, reason) => createExport(request, deviceId, from, to, format, reason)}
      onDone={() => {
        void qc.invalidateQueries({ queryKey: exportsKey });
        notify('Exportação iniciada. Acompanhe em Evidências → Exportações.');
      }}
    />
  );
}

/** Releases a hold: its recordings follow retention again. */
export function ReleaseDialog({ hold, onClose }: { hold: Hold; onClose: () => void }) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () => releaseHold(request, hold.id, reason.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: holdsKey });
      notify('Evidência liberada. As gravações voltam a seguir a retenção.');
      onClose();
    },
  });
  return (
    <Modal
      title="Liberar evidência"
      icon={<ShieldOff aria-hidden="true" className="size-4" strokeWidth={2} />}
      danger
      onClose={onClose}
      onSubmit={() => {
        setTried(true);
        if (!reasonError(reason)) save.mutate();
      }}
      submitLabel="Liberar"
      busy={save.isPending}
    >
      <p className="text-[13px] leading-relaxed text-fg-muted">
        As gravações deste intervalo voltam a seguir a retenção da câmera. As que já passaram do
        prazo serão apagadas na próxima limpeza e não poderão ser recuperadas.
      </p>
      <ReasonField
        label="Motivo da liberação"
        hint="Ex.: caso encerrado, material entregue. Fica registrado na auditoria."
        value={reason}
        error={tried ? reasonError(reason) : undefined}
        onChange={setReason}
      />
      <FormProblem message={save.isError ? problemOf(save.error) : null} />
    </Modal>
  );
}
